<template>
  <BaseField
    ref="field"
    class="field-select"
    :class="modifiers"
    v-bind="attributes"
    v-on="$listeners"
  >
    <template #container>
      <input
        ref="search"
        v-model="query"
        type="text"
        :placeholder="searchPlaceholder"
        :disabled="isEmpty"
        class="field__el field-select__search"
        @focus="onSearchFocused"
        @blur="onSearchBlured"
      />
      <button
        type="button"
        :disabled="isEmpty"
        class="field-select__toggle"
        @click="togglePopup"
      />
      <button
        v-if="isPopupVisible"
        type="button"
        class="field-select__overlay"
        @click="onOverlayClick"
      />
      <BaseSpinner
        v-if="isSpinnerVisible"
        class="field-select__spinner"
      />
      <div v-if="isPopupVisible" class="field-select__popup pretty-scrollbar">
        <button
          v-for="option in visibleOptions"
          :key="`field_select_option_${option.id}`"
          type="button"
          :disabled="option.disabled"
          class="field-select__option"
          :class="{
            'field-select__option_selected': option.id === selectedId
          }"
          @click="selectItem(option)"
        >
          <span v-html="option.label" />
        </button>
        <BaseAlert
          v-if="notfound"
          v-bind="notfound"
          class="field-select__alert"
        />
      </div>
    </template>
  </BaseField>
</template>

<script>
import BaseField from '@/components/BaseField.vue';
import BaseAlert from '@/components/BaseAlert.vue';
import BaseSpinner from '@/components/BaseSpinner.vue';

export default {
  name: 'FieldSelect',
  inheritAttrs: false,
  components: {
    BaseField,
    BaseAlert,
    BaseSpinner,
  },
  props: {
    options: {
      type: Array,
      default: () => [],
    },
    searchPlaceholder: {
      type: String,
      default() {
        return this.$t('fields.searchPlaceholder');
      },
    },
    emptyPlaceholder: {
      type: String,
      default() {
        return this.$t('fields.emptyPlaceholder');
      },
    },
  },
  data() {
    return {
      query: '',
      isSearchFocused: false,
      isSpinnerVisible: false,
      isPopupVisible: false,
    };
  },
  staticData: () => ({
    blurTimeout: null,
  }),
  computed: {
    attributes() {
      return {
        type: 'text',
        required: true,
        tabindex: '-1',
        hasClearButton: false,
        readonly: true,
        ...this.$attrs,
        placeholder: this.placeholder,
        value: this.$attrs.value?.label || '',
      };
    },
    modifiers() {
      return {
        'field-select_popup_visible': this.isPopupVisible,
        'field-select_pending': this.isSpinnerVisible,
        'field-select_empty': this.isEmpty,
      };
    },
    placeholder() {
      if (this.isSpinnerVisible) return this.$t('pending.fetch');
      if (this.isEmpty) return this.emptyPlaceholder;
      return this.$attrs.placeholder;
    },
    isEmpty() {
      return !this.isSpinnerVisible && this.options.length === 0;
    },
    isOnlyOneOption() {
      return this.options.length === 1;
    },
    visibleOptions() {
      const query = this.query.toLowerCase();
      return this.options.filter((el) => el.label.toLowerCase().includes(query));
    },
    selectedId() {
      return this.$attrs.value?.id;
    },
    notfound() {
      if (this.query && this.visibleOptions.length === 0) {
        return {
          icon: {
            name: 'important',
            theme: 'secondary',
            size: 'xs',
          },
          title: {
            text: this.$t('texts.notfound', { query: this.query }),
            size: 's',
          },
          actions: [],
          'data-animation': '',
        };
      }
      return null;
    },
  },
  beforeDestroy() {
    this.clearBlurTimeout();
  },
  methods: {
    focus() {
      return this.$refs.search?.focus();
    },
    blur() {
      return this.$refs.search?.blur();
    },
    checkValidity() {
      return this.$refs.field?.checkValidity();
    },
    checkHasValue() {
      return this.$refs.field?.checkHasValue();
    },
    selectItem(item) {
      this.hidePopup();
      this.clearQuery();
      return this.$emit('input', item);
    },
    onOverlayClick() {
      if (this.isSearchFocused) return;
      this.hidePopup();
    },
    onSearchFocused() {
      this.isSearchFocused = true;
      this.showPopup();
    },
    onSearchBlured() {
      this.blurTimeout = window.setTimeout(() => {
        this.isSearchFocused = false;
      }, 250);
    },
    clearBlurTimeout() {
      window.clearTimeout(this.blurTimeout);
    },
    clearQuery() {
      this.query = '';
    },
    togglePopup() {
      this.isPopupVisible = !this.isPopupVisible;
    },
    showPopup() {
      this.isPopupVisible = true;
    },
    hidePopup() {
      this.isPopupVisible = false;
    },
    showSpinner() {
      this.isSpinnerVisible = true;
    },
    hideSpinner() {
      this.isSpinnerVisible = false;
    },
  },
};
</script>

<style lang="stylus">
.field-select

  &_empty
    .field__el
      background-color alpha($c_text_dim, 10%)

  &__search
    abs top left right
    opacity 0
    &:focus
    ~/_popup_visible &
      opacity 1
      z-index 6

  &__toggle
    abs top 50% right
    display flex
    align-items center
    justify-content center
    width 2em
    height @width
    background none
    transform translate(-0.6em, -50%)
    border-radius 0.1em
    padding 0
    margin 0
    border 0
    outline none
    z-index 6
    &:after
      content ''
      width 0
      height 0
      border solid transparent
      border-width 0.36em 0.26em 0 0.26em
      border-top-color $c_text_dim
      transition transform 300ms cubic-bezier(.455,.03,.515,.955)
      ~/_popup_visible &
        transform rotateX(180deg)
    ~/_pending &
    &[disabled]
      opacity 0

  &__overlay
    fix top left
    width 100%
    height 100%
    padding 0
    margin 0
    border 0
    background alpha($c_text, 5%)
    z-index 5

  &__popup
    abs top 100% left right
    display block
    background-color #fff
    box-shadow 0 0.1em 1em -0.1em alpha(#000, 15%)
    border-radius 0.2em
    margin-top 0.15em
    margin-bottom 2em
    max-height 12.8em
    z-index 7

  &__alert
    padding 1.5em
    .alert__icon
      margin-bottom 0.6em

  &__spinner
    abs top left right bottom
    justify-content flex-end
    background-color alpha($c_dim, 30%)
    border-radius inherit
    color darken($c_dim, 40%)
    z-index 10

  &__option
    display block
    width 100%
    background none
    border 0
    padding 0
    margin 0
    text-align left
    border-top solid 1px alpha($c_dim, 30%)
    padding 1em 1.2em
    outline none
    &:first-child
      border-top-color transparent
    &:before
      content ''
      display inline-block
      vertical-align middle
      width 1em
      height 0.5em
      border solid currentColor
      border-width 0 0 2px 2px
      transform rotate(-45deg)
      margin-top (@height * -1)
      margin-right 1em
      transition opacity 250ms
      opacity 0
    &_selected
      color $c_primary
      &:before
        opacity 1
</style>
